import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDevice } from "./DeviceContext";
import { fetchUserProfile } from "../containers/UserInfo/slice";
import {
  fetchUserBackgrounds,
  setSurveyComplete,
} from "../containers/Dashboard/slice";

/**
 * Application Context for managing global app state
 * Provides device detection, theme management, sidebar state, authentication, and user data
 */
const AppContext = createContext();

/**
 * Helper function to determine initial sidebar state based on device type
 * @param {boolean} isMobile - Whether the current device is mobile
 * @param {boolean} isTablet - Whether the current device is tablet
 * @returns {boolean} - True if sidebar should be collapsed (hidden), false if expanded (visible)
 */
const getInitialSidebarState = (isMobile, isTablet) => {
  // Hide sidebar by default on mobile and tablet
  if (isMobile || isTablet) {
    return true; // collapsed = true means hidden
  }
  // Show sidebar by default on desktop
  return false; // collapsed = false means visible
};

/**
 * AppProvider component that wraps the entire application
 * Manages global state including authentication, user profile,
 * theme settings, sidebar state, and app initialization
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} - Provider component with context value
 */
export const AppProvider = ({ children }) => {
  // Redux state selectors
  const { token } = useSelector((state) => state.login);
  const { isSurveyComplete } = useSelector((state) => state.dashboard);
  const { profile: reduxUserProfile } = useSelector(
    (state) => state.userProfile
  );
  const dispatch = useDispatch();

  // Refs for tracking initialization state
  const hasInitialized = useRef(false);

  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAppInitialized, setIsAppInitialized] = useState(false);

  // User profile state (local copy of Redux state)
  const [localUserProfile, setLocalUserProfile] = useState(null);

  // Device detection from DeviceContext
  const { isMobile, isTablet, isDesktop, windowWidth } = useDevice();

  // Theme management states
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Sidebar management states
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    getInitialSidebarState(isMobile, isTablet)
  );

  // Network status state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  /**
   * Effect to update sidebar state when device type changes
   * Automatically collapses sidebar on mobile/tablet and expands on desktop
   */
  useEffect(() => {
    const shouldBeCollapsed = getInitialSidebarState(isMobile, isTablet);
    setIsSidebarCollapsed(shouldBeCollapsed);
  }, [isMobile, isTablet]);

  /**
   * Effect to sync Redux user profile with local state
   * Keeps local profile state in sync with Redux store
   */
  useEffect(() => {
    if (reduxUserProfile) {
      setLocalUserProfile(reduxUserProfile);
    }
  }, [reduxUserProfile]);

  /**
   * Effect to initialize app data when user is authenticated
   * Fetches user profile and background survey data
   * Sets survey completion status based on background data
   * Note: 401 handling is done in fetchUserProfile thunk
   */
  useEffect(() => {
    const initializeApp = async () => {
      if (token && !hasInitialized.current) {
        hasInitialized.current = true;
        setIsAppInitialized(false);

        try {
          // Fetch both profile and backgrounds concurrently
          const [, backgroundsResult] = await Promise.all([
            dispatch(fetchUserProfile()),
            dispatch(fetchUserBackgrounds()),
          ]);

          // Check if user has completed the survey based on backgrounds
          if (
            backgroundsResult.payload &&
            backgroundsResult.payload.length > 0
          ) {
            dispatch(setSurveyComplete(true));
          } else {
            dispatch(setSurveyComplete(false));
          }

          setIsAppInitialized(true);
        } catch (error) {
          setIsAppInitialized(true); // Still mark as initialized even on error
        }
      } else if (!token) {
        // Reset initialization state when logged out
        hasInitialized.current = false;
        setIsAppInitialized(true); // No need to initialize when not authenticated
        setLocalUserProfile(null); // Clear user profile when logged out
      }
    };

    initializeApp();
  }, [token, dispatch]);

  /**
   * Effect to update authentication state based on token presence
   */
  useEffect(() => {
    setIsAuthenticated(!!token);
    setIsAuthLoading(false);
  }, [token]);

  /**
   * Effect to set up network status event listeners
   * Handles online/offline status changes
   */
  useEffect(() => {
    /**
     * Handle network status changes
     */
    const handleOnlineStatus = () => {
      setIsOffline(!navigator.onLine);
    };

    // Initial setup
    handleOnlineStatus();

    // Event listeners
    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, []);

  /**
   * Function to update user profile in local state
   * @param {Object} updatedProfile - Updated user profile data
   */
  const updateUserProfile = (updatedProfile) => {
    setLocalUserProfile(updatedProfile);
  };

  /**
   * Context value object containing all app-wide state and functions
   */
  const value = {
    // Device information and responsive breakpoints (from DeviceContext)
    device: {
      isMobile, // true if window width < 768px
      isTablet, // true if window width >= 768px && <= 1024px
      isDesktop, // true if window width > 1024px
      windowWidth, // current window width in pixels
    },

    // Theme management
    theme: {
      isDarkMode, // current theme state
      toggleDarkMode: () => setIsDarkMode((prev) => !prev), // function to toggle theme
    },

    // Sidebar state management
    sidebar: {
      isCollapsed: isSidebarCollapsed, // current sidebar state
      toggle: () => setIsSidebarCollapsed((prev) => !prev), // function to toggle sidebar
    },

    // App status information
    status: {
      isOffline, // network connectivity status
    },

    // Authentication state
    auth: {
      isAuthenticated, // whether user is logged in
      isAuthLoading, // whether auth state is being determined
      isAppInitialized, // whether app data has been loaded
    },

    // Survey completion status
    survey: {
      isCompleted: isSurveyComplete, // whether user has completed background survey
    },

    // User profile data and management
    user: {
      profile: localUserProfile, // current user profile data
      updateUserProfile, // function to update profile
    },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

/**
 * Custom hook to access the App Context
 * Must be used within an AppProvider component
 *
 * @returns {Object} - The complete app context value
 * @throws {Error} - If used outside of AppProvider
 *
 * @example
 * const { device, theme, sidebar, auth, user } = useApp();
 *
 * // Access device information
 * if (device.isMobile) {
 *   // Mobile-specific logic
 * }
 *
 * // Toggle sidebar
 * sidebar.toggle();
 *
 * // Check authentication
 * if (auth.isAuthenticated) {
 *   // Authenticated user logic
 * }
 */
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
